import { Object3D, PerspectiveCamera, Vector3 } from 'three';
import type { LightDirector } from '../render/light-director';
import type { StaticLight } from '../render/bake-light';
import { BALANCE } from '../sim/balance';
import { ITEMS, item } from '../sim/items';
import { encumbrance, speedMultiplier, useConsumable } from '../sim/inventory';
import { Round, type RoundEvent, type ScoreLine } from '../sim/round';
import { mintSeed } from '../sim/rng';
import { SecuritySystem, type CameraDef } from '../sim/security';
import type {
  GuardStatus,
  ItemId,
  PlayerState,
  RoundPhase,
  ThiefStatus,
  Vec3,
} from '../sim/types';
import { LOCK_COLOR } from '../world/fixtures';
import { lootProp } from '../world/loot-props';
import type { BuiltMall, CameraPlan } from '../world/plan';
import { GUARD_MODES } from '../sim/guard-ai';
import { encodeVoice, decodeVoice } from '../net/protocol';
import {
  Btn,
  PFlag,
  makeCommand,
  type Command,
  type NetEvent,
  type NetGuard,
  type NetPlayer,
  type Snapshot,
  type Team,
} from '../net/protocol';
import type { WorkKind } from '../audio/cues';
import { VoiceChat } from '../audio/voice';
import { AudioDirector, tryDirector } from './audio-director';
import type { Emitter } from '../audio/engine';
import { Actor, Crew, seqNewer } from './crew';
import { ExposureField } from './exposure';
import { GuardCorps, type RadioLine, type ThiefSense } from './guards';
import { Hud, type NightBrief, type ObjectiveHud, type ResultsExtras, type VoiceHud } from './hud';
import { objectiveFor } from './objective';
import { Input, type Action } from './input';
import { Interaction, type InteractionResult, type Target } from './interaction';
import { NavGrid } from './navgrid';
import { MallPhysics, initPhysics } from './physics';
import { Player, type PlayerFrame } from './player';
import type { HandWork, Viewmodel } from './viewmodel';

/**
 * THE SESSION — where the building, the rules and the person meet.
 *
 * Everything under `src/sim/` is pure and knows nothing about three.js;
 * everything under `src/world/` is geometry and knows nothing about the rules.
 * This is the only file allowed to know both, and its whole job is to keep them
 * agreeing: the physics capsule's position is written into the sim's player
 * state every frame, and the sim's decisions are written back out as meshes
 * appearing, shutters vanishing and numbers moving on the HUD.
 *
 * It is deliberately the host. In single player the host is this machine and the
 * only client is this machine, and when the netcode arrived it replaced exactly
 * one thing — where the other players' positions come from. Everything else in
 * here runs identically in all three roles, which is why a solo round and a
 * hosted one cannot drift apart in behaviour.
 */

/**
 * Which machine this is.
 *
 * `solo` is not "host with nobody connected" — it is host with the network
 * layer absent entirely, which is what keeps single player free of a class of
 * bug it has no business being exposed to. `host` simulates everyone. `client`
 * simulates itself and believes what it is told about everything else.
 */
export type Role = 'solo' | 'host' | 'client';

export interface SessionOptions {
  mall: BuiltMall;
  camera: PerspectiveCamera;
  surface: HTMLElement;
  /** The scene node loose loot is parented to. Kept out of the static batch. */
  stage: Object3D;
  hudParent: HTMLElement;
  /** Guard torches compete for the same four dynamic light slots as everything else. */
  lights: LightDirector;
  /**
   * The shop fittings, after the lightmapper took them out of the scene. Used
   * to work out whether a thief is standing somewhere dark. Optional so a test
   * harness can build a session without a light rig; concealment is simply off
   * when it is missing, rather than being silently total.
   */
  staticLights?: readonly StaticLight[];
  /**
   * Your hands. Optional so the bot clients and the headless harness — neither
   * of which has a WebGL context — build a session without one.
   */
  viewmodel?: Viewmodel;
  /** Defaults to solo. The net layer passes host or client. */
  role?: Role;
  /** This machine's player id and slot. Slot 0 is always the host. */
  selfId?: string;
  selfSlot?: number;
  selfName?: string;
  /**
   * Force tonight's seed instead of minting one.
   *
   * For `?seed=` on the URL: the QA harness needs the same building every run to
   * be able to assert on it at all, and a crew that had a good night wants to be
   * able to play it again. Ignored by clients, who are handed the host's.
   */
  seed?: number;
}

/** Where the loadout is laid out, in world space. Matches the bench in mall.ts. */
const BENCH = { x: -2.0, z: 28.2, y: 0.885 };
/** Personal best, in dollars. The only thing that survives a page reload. */
const BEST_KEY = 'meridian.best';

const BENCH_TOOLS: ItemId[] = ['crowbar', 'lockpick', 'grinder', 'keycard', 'drill'];

/**
 * What a job looks like, given what it sounds like.
 *
 * The interaction layer already labels every fixture with the sound of working
 * on it, and that label is exactly the distinction the hands need — the same
 * split, for the same reason. Rather than add a second near-identical field to
 * `Target` that could disagree with the first, the hands read the audio's.
 */
const HAND_WORK: Record<WorkKind, HandWork> = {
  none: 'grab',
  take: 'grab',
  force: 'pry',
  pick: 'pick',
  card: 'card',
  cut: 'cut',
  safe: 'drill',
  deposit: 'give',
  leave: 'give',
};

/** Fixed ticks between light samples. Six is 10Hz, which nobody can outrun. */
const EXPOSURE_TICKS = 6;
/** How far the reported value moves toward a fresh sample. See `exposureOf`. */
const EXPOSURE_EASE = 0.34;
const BENCH_EXTRAS: ItemId[] = ['energyDrink', 'painkillers', 'jammer', 'feltPads'];

export class Session {
  readonly round: Round;
  readonly physics: MallPhysics;
  /** Everyone in the round, including this machine's own player. */
  readonly crew = new Crew();
  readonly role: Role;
  readonly input: Input;
  readonly hud: Hud;
  /** Where anything without hands can walk. Guards path over this. */
  readonly nav: NavGrid;
  /** The three night guards, their brains, bodies and torches. */
  readonly guards: GuardCorps;
  /** Fixed cameras. They cannot chase you; they tell someone who can. */
  readonly security: SecuritySystem;
  /** Null when the platform has no Web Audio. The game plays on regardless. */
  readonly audio: AudioDirector | null;
  /**
   * Proximity speech. Null without Web Audio or WebCodecs, and silent until
   * something calls `openMic` — a browser will not hand over a microphone
   * because a page loaded, and it should not.
   */
  readonly voice: VoiceChat | null;
  interaction: Interaction;

  /** False while the review camera has the scene. The harness sets this. */
  active = true;

  private readonly mall: BuiltMall;
  private readonly camera: PerspectiveCamera;
  /** Null in the bot clients and the headless harness. See `SessionOptions`. */
  private readonly viewmodel: Viewmodel | null;
  private readonly stage: Object3D;
  /** This machine's player id. `you` in solo, a slot id on the wire. */
  private readonly me: string;
  private readonly local: Actor;
  /** Client: rising sequence number stamped on every command we send. */
  private seq = 0;
  /** The commands we have sent and not yet seen acknowledged. */
  readonly pending: Command[] = [];
  /** Reused every step, so sampling input at 60Hz allocates nothing. */
  private readonly scratch: Command = makeCommand();
  private readonly eye = new Vector3();
  private readonly aim = new Vector3();
  /** Somebody else's facing, borrowed for a frame. Never the camera's. */
  private readonly remoteAim = new Vector3();
  private target: Target | null = null;
  /** Host: things that happened which a client cannot deduce from a snapshot. */
  private readonly netOut: NetEvent[] = [];
  /** Host: whose bag changed since the last drain. */
  private readonly invDirty = new Set<string>();
  /** Client: key presses the host has not been told about yet. */
  private readonly actionsOut: string[] = [];
  /** Encoded speech waiting for the next net pump. */
  private readonly voiceOut: ArrayBuffer[] = [];
  /** A panner per person who has spoken, kept so it can be moved with them. */
  private readonly voiceEmit = new Map<string, Emitter>();
  private voiceSeq = 0;
  /** Countdown to the next line-of-sight test for every speaking mouth. */
  private voiceOcclude = 0;
  /** One-element scratch, so a relayed radio line allocates nothing. */
  private readonly radioIn: RadioLine[] = [{ id: 'net', line: '', at: { x: 0, y: 0, z: 0 } }];
  private looseNodes = new Map<string, Object3D>();
  private accumulator = 0;
  private restartArmed = false;
  /** Reused each tick so the guards' senses allocate nothing. */
  private readonly senses: ThiefSense[] = [];
  private readonly thieves: PlayerState[] = [];
  private detection = 0;
  /** Null when the session was built without a light rig. See `SessionOptions`. */
  private readonly exposureField: ExposureField | null;
  /**
   * Last sampled exposure per thief, and the countdown to the next sample.
   *
   * Cached because the sample costs raycasts and nothing in the building moves
   * fast enough to need them sixty times a second — a player crosses about
   * eight centimetres between samples at a sprint. Counted in ticks rather than
   * seconds so the schedule is identical on every machine that re-simulates it.
   */
  private readonly exposure = new Map<string, number>();
  private exposureIn = 0;
  /** Set once per fixed step by `tickExposure`; read by `tickGuards`. */
  private exposureResample = false;
  private lastSay = '';
  /** Metres the player covered across this frame's fixed steps. Drives the gait. */
  private travelled = 0;
  /** Rebuilt in place each frame so the audio layer allocates nothing. */
  private readonly guardAudio: { id: string; pos: Vec3; speed: number; mode: string }[] = [];
  private readonly up = new Vector3(0, 1, 0);
  /** From `?seed=`. Survives a restart, so a pinned night stays pinned. */
  private readonly pinnedSeed: number | undefined;
  /** Last frame's yaw and pitch, so the hands can lag the head. */
  private readonly lastLook: [number, number] = [0, 0];

  private constructor(opts: SessionOptions, physics: MallPhysics) {
    this.mall = opts.mall;
    this.camera = opts.camera;
    this.viewmodel = opts.viewmodel ?? null;
    this.stage = opts.stage;
    this.physics = physics;
    this.role = opts.role ?? 'solo';
    this.me = opts.selfId ?? 'you';
    this.input = new Input(opts.surface);
    this.hud = new Hud(opts.hudParent);
    this.pinnedSeed = opts.seed;
    this.exposureField = opts.staticLights?.length
      ? new ExposureField(opts.staticLights, physics)
      : null;

    const spawn = opts.mall.spawns.thieves[0] ?? { x: 0, y: 0, z: 30 };
    // The local player is an actor like everybody else. Making it a special
    // field that the crew does not know about is how a codebase ends up with
    // two movement paths that drift apart, and this one has exactly one.
    this.local = this.crew.add({
      id: this.me,
      slot: opts.selfSlot ?? 0,
      name: opts.selfName ?? 'You',
      team: 'thief',
      local: true,
      simulated: true,
      spawn,
      stage: opts.stage,
      physics,
    });
    this.round = this.newRound(spawn, opts.seed);
    this.interaction = new Interaction(opts.mall, physics, this.round);
    this.nav = new NavGrid(
      opts.mall.zones,
      opts.mall.colliders,
      opts.mall.stairs,
      opts.mall.barriers.map((b) => ({ ...b.collider, id: b.id })),
    );
    this.layOutBench();

    this.security = new SecuritySystem(opts.mall.cameras.map(cameraDef));
    this.guards = new GuardCorps({
      physics,
      nav: this.nav,
      round: this.round,
      zones: opts.mall.zones,
      routes: opts.mall.patrols,
      spawns: opts.mall.spawns.guards,
      stage: opts.stage,
      lights: opts.lights,
    });

    // The director gets the floor plan (which surface am I standing on) and one
    // line-of-sight predicate (is there a wall between the ear and that guard).
    // Deliberately nothing else: handing it the session would give the audio
    // layer the run of the game and make the import cycle real.
    this.audio = tryDirector({
      zones: opts.mall.zones,
      clear: (from, to) => this.canSee(from, to),
    });

    // Speech comes out of a panner attached to the speaker's body, exactly like
    // their footsteps do, which is the entire point: a voice with no direction
    // is a voice from outside the building. `route` returns null until that body
    // exists, and voice asks again on the next packet — a frame of speech
    // regularly beats the snapshot that creates the person saying it.
    const ctx = this.audio?.engine.ctx;
    this.voice =
      ctx && VoiceChat.supported
        ? new VoiceChat({
            ctx,
            route: (id) => {
              if (!this.audio || !this.crew.get(id)) return null;
              const em = this.audio.engine.emitter(`voice:${id}`);
              this.voiceEmit.set(id, em);
              return em.input;
            },
            onFrame: (payload) => {
              this.voiceSeq = (this.voiceSeq + 1) & 0xffff;
              this.voiceOut.push(encodeVoice(this.voiceSeq, payload));
            },
          })
        : null;

    // Clicking the canvas is how you take control; Escape gives it back. The
    // pause card is not a menu, it is the thing that explains the keys, so it
    // is shown whenever the pointer is loose rather than only on first load.
    opts.surface.addEventListener('mousedown', () => {
      if (this.active && !this.input.locked && this.round.phase !== 'results') this.input.requestLock();
    });
  }

  /**
   * This machine's capsule.
   *
   * A getter over the local actor rather than a field, so there is one owner of
   * the body and no way for the two to disagree after a respawn.
   */
  get player(): Player {
    return this.local.body!;
  }

  /** This machine's player id, so the net layer can address it. */
  get selfId(): string {
    return this.me;
  }

  static async create(opts: SessionOptions): Promise<Session> {
    await initPhysics();
    const barriers = opts.mall.barriers.map((b) => b.collider);
    const physics = new MallPhysics(opts.mall.colliders, barriers);
    return new Session(opts, physics);
  }

  // -----------------------------------------------------------------------

  private newRound(spawn: Vec3, seed?: number): Round {
    const r = new Round({ seed });
    // Everyone in the lobby is enrolled, not just this machine's player, so a
    // mid-session restart does not silently drop the rest of the crew out of
    // the scoreboard while their bodies keep walking around.
    for (const a of this.crew.all) {
      const at = a.local ? spawn : { x: a.pos.x, y: a.pos.y, z: a.pos.z };
      r.addPlayer(a.id, a.name, a.team, at);
      if (a.body) a.body.teleport(at, a.body.yaw);
    }
    if (!r.players.size) r.addPlayer(this.me, 'You', 'thief', spawn);
    r.stockContainers(
      this.mall.containers.map((c) => ({ id: c.id, store: c.store, lock: c.lock })),
    );
    for (const d of this.mall.dropoffs) r.addDropoff({ id: d.id, kind: d.kind, pos: d.pos, radius: d.radius });
    r.start();
    return r;
  }

  /** What the briefing card reads off. Cheap enough to rebuild every frame. */
  private get night(): NightBrief {
    return { name: this.round.name, consignment: this.round.consignment };
  }

  /**
   * The middle of tonight's consignment store, in world space.
   *
   * The sim knows which store got the delivery and nothing about where anything
   * is — `stockContainers` is handed ids and lock kinds, deliberately, so the
   * round can be simulated headless. Positions live out here, so the waypoint is
   * resolved out here too rather than plumbing geometry into the sim to save one
   * lookup. Cached per store because it cannot change inside a night.
   */
  private consignmentCache: { store: string; at: Vec3 | null } | null = null;
  private get consignmentAt(): Vec3 | null {
    const store = this.round.consignment?.store;
    if (!store) return null;
    if (this.consignmentCache?.store === store) return this.consignmentCache.at;
    let n = 0;
    const sum = { x: 0, y: 0, z: 0 };
    for (const c of this.mall.containers) {
      if (c.store !== store) continue;
      sum.x += c.pos.x;
      sum.y += c.pos.y;
      sum.z += c.pos.z;
      n++;
    }
    // Averaged rather than "first cabinet", so the arrow points at the shop
    // instead of at whichever shelf happened to be authored first — which in a
    // long store is a diagonal across the whole unit.
    const at = n ? { x: sum.x / n, y: sum.y / n + 1.2, z: sum.z / n } : null;
    this.consignmentCache = { store, at };
    return at;
  }

  /**
   * Turn the objective into something the HUD can draw, including where on the
   * screen it lands.
   *
   * PROJECTION IS THE WHOLE JOB HERE. `Vector3.project` divides by w, which for
   * a point behind the camera is negative — so the naive version puts the
   * waypoint for the shop behind you on the opposite side of the screen and
   * confidently tells you to run the wrong way. Camera space is checked first
   * and the projected point is flipped when it is behind, then anything outside
   * the frame is pushed back onto the edge along its own direction so the marker
   * degrades into a "turn this way" arrow rather than disappearing.
   */
  private objectiveHud(p: PlayerState): ObjectiveHud | null {
    const o = objectiveFor({
      phase: this.round.phase,
      clock: this.round.clock,
      player: p,
      consignment: this.round.consignment,
      consignmentAt: this.consignmentAt,
      bench: { x: BENCH.x, y: BENCH.y + 0.35, z: BENCH.z },
      dropoffs: this.round.dropoffs,
      thievesLeft: this.round.thievesRemaining(),
    });
    if (!o) return null;

    const base = { verb: o.verb, detail: o.detail, urgent: o.urgent, mark: o.mark };
    if (!o.at) return { ...base, distance: 0, screen: null };

    const at = this.objTmp.set(o.at.x, o.at.y, o.at.z);
    const distance = at.distanceTo(this.camera.position);
    const behind = this.objTmp2.copy(at).applyMatrix4(this.camera.matrixWorldInverse).z > -0.05;
    at.project(this.camera);
    let x = behind ? -at.x : at.x;
    let y = behind ? -at.y : at.y;

    // The frame the edge marker is pinned inside. Not the viewport: the corners
    // of the viewport already belong to the money, the slots and the shade bars,
    // and a waypoint printed through the weight readout is two unreadable things
    // instead of one. An edge arrow only has to convey a bearing, so it can sit
    // well inside the glass and lose nothing.
    const MX = 0.88;
    const MY = 0.62;
    const angle = Math.atan2(-y, x);
    const edge = behind || Math.abs(x) > MX || Math.abs(y) > MY;
    if (edge) {
      // Push out to the boundary along the same bearing. Scaling by the tighter
      // of the two axis ratios lands exactly on the rectangle, corners included.
      const k = Math.min(MX / Math.max(1e-4, Math.abs(x)), MY / Math.max(1e-4, Math.abs(y)));
      x *= k;
      y *= k;
    }
    return {
      ...base,
      distance,
      screen: { x: x * 0.5 + 0.5, y: 0.5 - y * 0.5, edge, angle },
    };
  }

  private readonly objTmp = new Vector3();
  private readonly objTmp2 = new Vector3();

  /** Puts the loadout on the table. Tools front row, consumables behind. */
  private layOutBench(): void {
    // 62cm apart, which is as wide as the bench allows. Any tighter and the
    // crosshair cannot separate two tools from a pace back, and the round opens
    // by picking your loadout for you.
    BENCH_TOOLS.forEach((id, i) => {
      this.spawnLoose(id, { x: BENCH.x + (i - 2) * 0.62, y: BENCH.y, z: BENCH.z + 0.16 }, (i - 2) * 0.12);
    });
    BENCH_EXTRAS.forEach((id, i) => {
      this.spawnLoose(id, { x: BENCH.x + (i - 1.5) * 0.62, y: BENCH.y, z: BENCH.z - 0.22 }, 0.4);
    });
  }

  /** Mints a loose item in the sim and the mesh that stands for it, together. */
  private spawnLoose(id: ItemId, pos: Vec3, yaw = 0): void {
    const looseId = this.round.addLoose(id, pos);
    const node = lootProp(id);
    node.position.set(pos.x, pos.y, pos.z);
    node.rotation.y = yaw;
    this.stage.add(node);
    this.looseNodes.set(looseId, node);
    this.interaction.addLoose({ id: looseId, item: id, pos, store: null, node });
  }

  // -----------------------------------------------------------------------

  /**
   * One frame.
   *
   * The sim runs on the frame's own dt rather than a fixed tick because nothing
   * in it integrates — it counts down a clock and validates actions, both of
   * which are exact at any step size. The physics character controller is the
   * part that needs a stable step, and it gets one below.
   */
  update(dt: number): void {
    if (!this.active) return;
    const p = this.round.players.get(this.me)!;

    const results = this.round.phase === 'results';
    this.player.enabled = !results && p.status === 'active';
    if (results && this.input.locked) this.input.releaseLock();

    // Encumbrance and consumables, applied before movement so the frame the
    // 28kg suit of armour goes in your bag is the frame you slow down.
    const enc = encumbrance(p);
    this.player.speedScale = enc.speed * speedMultiplier(p);
    this.player.jumpScale = enc.jump;

    // Physics on a fixed step. A kinematic controller is a pure function of the
    // step it is given, so a frame-rate-dependent step means a player on a 144Hz
    // monitor climbs slopes a 30fps player cannot — and, once there is netcode,
    // desyncs from the host on exactly those slopes.
    this.accumulator = Math.min(0.25, this.accumulator + dt);
    const STEP = 1 / 60;
    // Look is sampled once per rendered frame, not once per fixed step. Mouse
    // motion arrives at frame rate, and quantising it to 60Hz on a 144Hz display
    // is felt immediately as drag even though the body is stepping correctly.
    let frame = this.player.step(0, this.player.sampleCommand(this.input, 0, this.scratch));
    this.player.writeCamera(this.camera);
    this.travelled = 0;
    while (this.accumulator >= STEP) {
      this.physics.step();
      const cmd = this.player.sampleCommand(this.input, this.nextSeq(), this.scratch);
      // A client keeps its own commands so it can tell, when the host's answer
      // comes back, which of its predictions that answer was replying to.
      if (this.role === 'client') {
        this.pending.push({ ...cmd });
        if (this.pending.length > 120) this.pending.shift();
      }
      frame = this.player.step(STEP, cmd);
      this.local.frame = frame;
      // Then nudge the capsule towards where the host last said it was. On the
      // step, not the frame, so the rate is the same on every machine.
      this.local.applyCorrection(STEP);
      this.player.writeCamera(this.camera);
      // Everyone else the host is responsible for, on the same step and through
      // the identical controller. This is the entire reason `Player.step` takes
      // a command instead of reading a keyboard.
      if (this.role !== 'client') this.stepRemotes(STEP);
      this.writeBackPositions();
      // Guards share the player's step for the same reason the player has one:
      // they run the identical character controller, and a guard integrated on
      // the frame's dt would take corners differently at 30fps and 144fps. It
      // also means their line of sight is sampled at a fixed rate, so awareness
      // fills at the same speed on every machine.
      // How lit you are is worked out on every machine, host or not. The light
      // rig is authored, identical everywhere, and sampled against the same
      // colliders, so a client can answer the question for itself — and it has
      // to, because the concealment readout in the corner of its screen would
      // otherwise be a guess.
      this.tickExposure();
      if (this.role !== 'client') this.tickGuards(STEP);
      // Summed over the steps, not read from the last one. At 144Hz the render
      // frame contains no physics step at all some frames and two others, and a
      // gait driven by the last step's distance would drop half the footsteps.
      this.travelled += frame.travelled;
      this.accumulator -= STEP;
    }

    this.writeBackPositions();

    this.guards.render(dt);
    // Bodies are posed after everything has finished moving, and on the frame's
    // own dt rather than the fixed step: animation is allowed to be smooth even
    // where simulation is not.
    const nowMs = this.clockMs();
    for (const a of this.crew.all) if (!a.local) a.render(dt, nowMs);
    // A client's clock is the host's, arriving in snapshots. Ticking it locally
    // as well would race the wire and make the last ten seconds of a round
    // disagree between machines, which is the ten seconds that matter.
    if (this.role !== 'client') this.round.tick(dt);
    for (const e of this.round.drainEvents()) this.onEvent(e);

    // --- interaction -------------------------------------------------------
    this.eye.copy(this.player.position);
    this.eye.y += this.player.eyeHeight;
    this.player.forward(this.aim);
    this.target = p.status === 'active' && !results ? this.interaction.find(this.eye, this.aim, p) : null;
    const held = this.input.isHeld('interact');
    // A client raycasts only to draw its own prompt. Whether the shutter
    // actually opens is the host's call, below, using the id the client sent.
    const outcome =
      this.role === 'client'
        ? null
        : this.interaction.update(dt, this.target, held && this.input.locked, p);
    if (outcome) this.onOutcome(outcome);
    if (this.role === 'host') this.tickRemoteInteraction(dt);

    for (const action of this.input.drainActions()) this.onAction(action);

    this.hud.update(dt, {
      phase: this.round.phase,
      clock: this.round.clock,
      night: this.night,
      player: p,
      target: this.target,
      progress: this.interaction.progressFor(this.me),
      stamina: frame.stamina,
      paused: !this.input.locked,
      thievesLeft: this.round.thievesRemaining(),
      detection: this.detection,
      exposure: this.myExposure,
      voice: this.voiceHud(),
      objective: this.objectiveHud(p),
    });

    this.tickViewmodel(dt, p, frame, held);
    this.tickAudio(dt, p, frame);
  }

  /**
   * Your own hands.
   *
   * Everything the viewmodel needs already exists on this frame — what you are
   * holding, what you are breaking into, how far through it you are and how lit
   * you are standing — so this is a translation and nothing more. The one piece
   * it cannot get from anywhere else is the look delta, which the command
   * sampler consumed on its way to the physics step; the camera's own change
   * since last frame is the same number and survives being read twice.
   */
  private tickViewmodel(dt: number, p: PlayerState, frame: PlayerFrame, held: boolean): void {
    const vm = this.viewmodel;
    if (!vm) return;

    const t = this.target;
    // Only while the bar is actually moving. Holding the key on a shutter you
    // have no grinder for is a refusal, and the hands should not mime cutting it.
    const working = held && this.input.locked && t !== null && !t.blocked && t.duration > 0;
    const work: HandWork | null = working ? HAND_WORK[t.work] : null;
    const progress = t && t.duration > 0 ? this.interaction.progressFor(this.me) / t.duration : 0;

    const yaw = this.player.yaw;
    const pitch = this.player.pitch;
    const dYaw = yaw - this.lastLook[0];
    const dPitch = pitch - this.lastLook[1];
    this.lastLook[0] = yaw;
    this.lastLook[1] = pitch;

    vm.update(dt, {
      visible: p.status === 'active' && this.round.phase !== 'results' && this.input.locked,
      speed: frame.speed,
      grounded: frame.grounded,
      sprinting: frame.sprinting,
      crouching: frame.crouching,
      yawDelta: dYaw,
      pitchDelta: dPitch,
      // The job's tool beats the loadout's, and the viewmodel decides that — all
      // it needs from here is what is actually in the bag.
      tool: p.tools[0] ?? null,
      loot: p.carried.length ? p.carried[p.carried.length - 1].item : null,
      work,
      progress: Math.min(1, Math.max(0, progress)),
      light: this.myExposure ?? 0.35,
    });
  }

  // ---------------------------------------------------------------------
  // The other people in the building.
  // ---------------------------------------------------------------------

  /** Wraps at 16 bits, because that is all the wire carries. */
  private nextSeq(): number {
    this.seq = (this.seq + 1) & 0xffff;
    return this.seq;
  }

  /**
   * A monotonic millisecond clock for interpolation.
   *
   * `performance.now` where it exists and `Date.now` where it does not, because
   * the QA harness runs a headless session in Node and a body that will not
   * interpolate there is a body no test can watch.
   */
  private clockMs(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  /** Host: run one fixed step of everyone who is not holding this keyboard. */
  private stepRemotes(step: number): void {
    for (const a of this.crew.all) {
      if (a.local || !a.body) continue;
      const p = this.round.players.get(a.id);
      // Enabled tracks the sim, exactly as it does for the local player: a
      // captured thief's capsule stops taking input but stays in the world, so
      // the guard standing over them has something to stand over.
      a.body.enabled = this.round.phase !== 'results' && p?.status === 'active';
      if (p) {
        const enc = encumbrance(p);
        a.body.speedScale = enc.speed * speedMultiplier(p);
        a.body.jumpScale = enc.jump;
        a.kg = enc.kg;
      }
      a.frame = a.body.step(step, a.nextCommand());
    }
  }

  /**
   * Host: run everyone else's held interact key.
   *
   * The client tells us two things — that interact is down, in the command
   * stream, and what its crosshair was on, out of band. Neither is trusted. We
   * re-run the same scoring raycast from the body WE simulated, and only if it
   * lands on the fixture the client named does the hold advance. A client that
   * lies about its target gets a null target and its progress decays; a client
   * that is honest but a hundred milliseconds stale gets the same, and recovers
   * the moment its aim and ours agree, which is the next packet.
   *
   * Cheap enough to do per frame for eight people: `find` is a scored loop over
   * a few hundred candidates with the only raycast at the end, on the winner.
   */
  private tickRemoteInteraction(dt: number): void {
    for (const a of this.crew.all) {
      if (a.local || !a.body) continue;
      const p = this.round.players.get(a.id);
      if (!p) continue;
      const held = (a.last.buttons & Btn.Interact) !== 0;
      const live = p.status === 'active' && this.round.phase !== 'results';
      let target: Target | null = null;
      if (held && live) {
        this.eye.copy(a.body.position);
        this.eye.y += a.body.eyeHeight;
        a.body.forward(this.aim);
        const found = this.interaction.find(this.eye, this.aim, p);
        if (found && `${found.kind}:${found.id}` === a.wants) target = found;
      }
      const outcome = this.interaction.update(dt, target, held && live, p);
      if (outcome) this.applyOutcome(outcome, target?.at ?? a.body.position, a);
    }
  }

  /** Copy every simulated capsule into the sim's idea of where people are. */
  private writeBackPositions(): void {
    for (const a of this.crew.all) {
      const p = this.round.players.get(a.id);
      if (!p) continue;
      const src = a.body ? a.body.position : a.pos;
      p.pos.x = src.x;
      p.pos.y = src.y;
      p.pos.z = src.z;
    }
  }

  /**
   * Someone joined.
   *
   * Called on every machine, with `simulated` true only where their body is
   * this machine's responsibility. The sim gets a player either way — the
   * scoreboard, the capture rules and the extraction count are all pure logic
   * and run identically everywhere, they simply take their positions from a
   * different source.
   */
  addPlayer(id: string, slot: number, name: string, team: Team): Actor {
    const spawn =
      team === 'guard'
        ? (this.mall.spawns.guards[slot % Math.max(1, this.mall.spawns.guards.length)] ?? origin)
        : (this.mall.spawns.thieves[slot % Math.max(1, this.mall.spawns.thieves.length)] ??
          this.mall.spawns.thieves[0] ??
          origin);
    if (!this.round.players.has(id)) this.round.addPlayer(id, name, team, spawn);
    const a = this.crew.add({
      id,
      slot,
      name,
      team,
      local: false,
      simulated: this.role !== 'client',
      spawn,
      stage: this.stage,
      physics: this.physics,
    });
    this.hud.toast(`${name} joined`, 'info', 3);
    return a;
  }

  removePlayer(id: string): void {
    const a = this.crew.get(id);
    if (!a) return;
    this.hud.toast(`${a.name} left`, 'info', 3);
    this.interaction.forget(id);
    this.crew.remove(id);
    this.round.players.delete(id);
  }

  /** Host: commands arriving from a client. */
  acceptCommands(from: string, cmds: readonly Command[]): void {
    this.crew.get(from)?.enqueue(cmds);
  }

  /** Host: what a client says it is aiming at. Validated when the hold runs. */
  acceptTarget(from: string, id: string | null): void {
    const a = this.crew.get(from);
    if (a) a.wants = id;
  }

  /**
   * Client: what to tell the host we are aiming at.
   *
   * `kind:id` rather than the bare id, because a container and a barrier in the
   * same unit can share a name and the host compares this string exactly.
   */
  get targetKey(): string | null {
    return this.target ? `${this.target.kind}:${this.target.id}` : null;
  }

  /** Host: an edge-triggered key press from a client. */
  acceptAction(from: string, action: string): void {
    if (this.crew.get(from)) this.onAction(action, from);
  }

  /** Client: key presses waiting to be asked for. */
  drainActionsOut(): string[] {
    return this.actionsOut.length ? this.actionsOut.splice(0, this.actionsOut.length) : EMPTY_ACTIONS;
  }

  /** Speech captured since the last pump. Both roles send; nobody validates it. */
  drainVoice(): ArrayBuffer[] {
    return this.voiceOut.length ? this.voiceOut.splice(0, this.voiceOut.length) : EMPTY_VOICE;
  }

  /**
   * Somebody else's speech.
   *
   * Accepted from anyone in the room with no check on team, distance or round
   * phase — deliberately. The host does not decide who can hear whom; the
   * panner does, by being twenty metres away. Filtering here would mean the
   * host silently censoring a conversation, and getting the radius wrong by two
   * metres would be indistinguishable from a broken microphone.
   */
  acceptVoice(from: string, data: ArrayBuffer): void {
    const v = decodeVoice(data);
    if (v) this.voice?.receive(from, v.seq, v.payload);
  }

  /**
   * Ask for the microphone. Returns false if the player says no, or if the
   * browser has no Opus encoder to offer.
   *
   * Separate from the constructor because a permission prompt on page load is
   * the fastest way to be denied one, and because the harness hands in a
   * synthesised stream instead of a device.
   */
  /**
   * One key for the whole feature.
   *
   * First press asks for the microphone, every press after that mutes and
   * unmutes it. Two separate controls would be more honest about what is
   * happening underneath and worse to use, because the thing a player wants at
   * three in the morning is one key that means "stop transmitting".
   */
  private toggleMic(): void {
    const v = this.voice;
    if (!v) return;
    if (!v.stats.on) {
      this.hud.toast('Mic…', 'info', 1.2);
      void this.openMic().then((ok) => {
        this.hud.toast(ok ? 'Mic live' : 'No mic', ok ? 'good' : 'bad', 2);
      });
      return;
    }
    v.muted = !v.muted;
    this.hud.toast(v.muted ? 'Mic off' : 'Mic live', v.muted ? 'info' : 'good', 1.6);
  }

  async openMic(source?: MediaStream): Promise<boolean> {
    return (await this.voice?.start(source)) ?? false;
  }

  // ---------------------------------------------------------------------
  // Events the wire has to carry
  // ---------------------------------------------------------------------

  /**
   * Why there is a second event stream at all.
   *
   * A snapshot answers "where is everyone and how are they doing". It cannot
   * answer "the jewellery vitrine is now open", because that is a fact about the
   * building, it happens once, and re-sending it twenty times a second forever
   * would cost more than the whole rest of the protocol. So state that changes
   * continuously goes in snapshots and state that changes at discrete moments
   * goes here, reliably and exactly once.
   *
   * The awkward middle case is inventory. It changes at discrete moments, but
   * the moments are frequent and a missed one leaves a client's HUD quietly
   * wrong for the rest of the round. It is handled by sending the whole bag,
   * not a delta, whenever it is dirty: an absolute value cannot accumulate drift.
   */
  private pushNet(e: NetEvent): void {
    if (this.role === 'host') this.netOut.push(e);
  }

  /** Say something to one player, wherever they are sitting. */
  private say(who: string, text: string, tone: 'good' | 'bad' | 'info', secs = 3): void {
    if (who === this.me) this.hud.toast(text, tone, secs);
    else this.pushNet({ t: 'toast', text, tone, for: who });
  }

  /** Host: everything a client needs told to it since the last call. */
  drainNet(): NetEvent[] {
    for (const id of this.invDirty) {
      const p = this.round.players.get(id);
      if (!p) continue;
      this.netOut.push({
        t: 'inv',
        who: id,
        carried: p.carried.map((s) => s.item),
        tools: [...p.tools],
        banked: p.banked,
      });
    }
    this.invDirty.clear();
    if (!this.netOut.length) return EMPTY_EVENTS;
    return this.netOut.splice(0, this.netOut.length);
  }

  /**
   * Client: something the host says happened.
   *
   * Deliberately tolerant. Every branch is written so that applying it to a
   * world where it has already happened, or where the thing it names is missing,
   * is a no-op rather than a throw — a client that joins mid-round gets a burst
   * of these and must not care about the order it learns things in.
   */
  applyNetEvent(e: NetEvent): void {
    switch (e.t) {
      case 'toast':
        if ((!e.for || e.for === this.me) && e.not !== this.me) this.hud.toast(e.text, e.tone, 3);
        break;
      case 'breach': {
        const c = this.round.containers.get(e.id);
        if (c) c.open = true;
        else {
          const b = this.mall.barriers.find((x) => x.id === e.id);
          if (b) {
            b.node.visible = false;
            this.physics.openBarrier(e.id);
          }
        }
        this.markOpen(e.id);
        this.nav.setBarrierOpen(e.id, true);
        break;
      }
      case 'looted': {
        const c = this.round.containers.get(e.container);
        if (c) {
          const i = c.contents.indexOf(e.item);
          if (i >= 0) c.contents.splice(i, 1);
          if (e.cleared) {
            c.contents.length = 0;
            this.emptyIfCleared(e.container);
          }
        } else {
          // Not a fixture, so it was something lying on the floor.
          this.interaction.takeLooseRemotely(e.container);
          this.looseNodes.delete(e.container);
        }
        break;
      }
      case 'banked':
        this.audio?.outcome(
          { kind: 'deposited', amount: e.amount, items: e.items, extraction: false },
          new Vector3(e.at[0], e.at[1], e.at[2]),
        );
        break;
      case 'loose':
        if (!this.looseNodes.has(e.id)) {
          const at = { x: e.at[0], y: e.at[1], z: e.at[2] };
          this.round.addLooseWithId(e.id, e.item, at);
          this.attachLoose(e.id, e.item, at);
        }
        break;
      case 'took':
        this.interaction.takeLooseRemotely(e.id);
        this.looseNodes.delete(e.id);
        break;
      case 'inv': {
        const p = this.round.players.get(e.who);
        if (!p) break;
        // Replaced wholesale rather than reconciled. The host's copy is the only
        // one that decides anything; this one exists to be drawn.
        p.carried.length = 0;
        for (const it of e.carried) p.carried.push({ item: it, store: null });
        p.tools.length = 0;
        p.tools.push(...e.tools);
        p.banked = e.banked;
        break;
      }
      case 'capture':
        if (e.who === this.me) this.hud.toast('Caught.', 'bad', 8);
        break;
      case 'guardDown':
        break;
      case 'radio':
        this.radioIn[0] = { id: 'net', line: e.line, at: { x: e.at[0], y: e.at[1], z: e.at[2] } };
        this.audio?.radio(this.radioIn);
        if (e.line !== this.lastSay) {
          this.lastSay = e.line;
          this.hud.toast(e.line, 'bad', 4);
        }
        break;
      case 'score':
        this.input.releaseLock();
        {
          const rows = e.rows.map((r) => ({
            id: r.id,
            name: r.name,
            team: r.team,
            banked: r.banked,
            unbanked: r.unbanked,
            status: r.status as ThiefStatus | GuardStatus,
          }));
          this.hud.showResults(rows, e.reason, this.me, this.reviewFor(rows, e.floor));
        }
        break;
      case 'reset':
        this.restart(e.seed);
        break;
      case 'phase':
        this.round.phase = PHASE_NAME[e.phase] ?? this.round.phase;
        break;
    }
  }

  /**
   * Host: the state of the world, as small as it goes.
   *
   * Rebuilt rather than pooled because it is produced twenty times a second and
   * the arrays are a dozen entries long; the allocation is noise next to the
   * encode, and a pooled snapshot handed to an encoder that keeps a reference is
   * a bug that only shows up under packet loss.
   */
  buildSnapshot(tick: number): Snapshot {
    const players: NetPlayer[] = [];
    for (const a of this.crew.all) {
      const p = this.round.players.get(a.id);
      const src = a.body ? a.body.position : a.pos;
      players.push({
        slot: a.slot,
        x: src.x,
        y: src.y,
        z: src.z,
        yaw: a.body ? a.body.yaw : a.yaw,
        pitch: a.body ? a.body.pitch : a.pitch,
        flags: a.flags | (this.interaction.progressFor(a.id) > 0 ? PFlag.Working : 0),
        status: STATUS_CODE[p?.status ?? 'active'] ?? 0,
        ack: a.ack,
        kg: p ? encumbrance(p).kg : 0,
      });
    }
    const guards: NetGuard[] = this.guards.guards.map((g, i) => ({
      slot: i,
      x: g.pos.x,
      y: g.pos.y,
      z: g.pos.z,
      yaw: g.yaw,
      mode: Math.max(0, GUARD_MODES.indexOf(g.brain.mode)),
      alert: g.brain.peakAlert,
    }));
    return {
      tick,
      time: Math.round(this.clockMs()),
      clock: this.round.clock,
      phase: PHASE_CODE[this.round.phase] ?? 0,
      players,
      guards,
    };
  }

  /**
   * Client: believe the host.
   *
   * Our own entry is reconciled against what we predicted; everybody else's is
   * pushed into their interpolation buffer. Nothing here moves a body directly —
   * that happens on the render step, a tenth of a second later, which is what
   * turns twenty transforms a second into something that walks.
   */
  applySnapshot(s: Snapshot): void {
    const now = this.clockMs();
    this.round.clock = s.clock;
    const phase = PHASE_NAME[s.phase];
    if (phase && this.round.phase !== phase) this.round.phase = phase;
    for (const np of s.players) {
      const a = this.crew.atSlot(np.slot);
      if (!a) continue;
      const p = this.round.players.get(a.id);
      if (p) p.status = STATUS_NAME[np.status] ?? p.status;
      if (a.local) {
        a.reconcile(np);
        // Everything the host has already answered can be forgotten.
        while (this.pending.length && !seqNewer(this.pending[0].seq, np.ack)) this.pending.shift();
      } else {
        a.push(np, now);
        // Eased rather than set, so a thief who gets caught folds up over a
        // third of a second instead of snapping to the floor between frames.
        const wantDown = p?.status === 'caught' || p?.status === 'downed' ? 1 : 0;
        a.down += (wantDown - a.down) * 0.25;
      }
    }
    for (const ng of s.guards) {
      this.guards.applyNet(ng.slot, ng.x, ng.y, ng.z, ng.yaw, ng.alert, GUARD_MODES[ng.mode] ?? 'patrol');
    }
  }

  /**
   * Hand the frame to the audio director.
   *
   * Last thing in the update, so every sound this frame is scheduled against a
   * world that has already finished moving. It reads state rather than being
   * pushed to, for one reason: the alternative is a `play()` call scattered
   * through nine methods of this file, and the first time somebody adds a tenth
   * they will forget one.
   */
  private tickAudio(dt: number, p: PlayerState, frame: { speed: number; grounded: boolean; crouching: boolean }): void {
    const a = this.audio;
    if (!a) return;
    this.guardAudio.length = 0;
    for (const g of this.guards.guards) {
      this.guardAudio.push({ id: g.id, pos: g.pos, speed: g.speed, mode: g.brain.mode });
    }
    a.frame({
      dt,
      eye: this.eye,
      forward: this.aim,
      up: this.up,
      feet: p.pos,
      travelled: this.travelled,
      grounded: frame.grounded,
      crouching: frame.crouching,
      speed: frame.speed,
      phase: this.round.phase,
      clock: this.round.clock,
      detection: this.detection,
      paused: !this.input.locked || this.round.phase === 'results',
      // Only while the key is actually down. A grinder that keeps running
      // because the player is merely looking at a shutter would be the loudest
      // lie in the game.
      work: this.target && !this.target.blocked && this.input.isHeld('interact') ? this.target.work : null,
      workProgress: this.interaction.progressFor(this.me),
      guards: this.guardAudio,
    });

    this.tickVoice(dt);
  }

  /**
   * Move everybody's mouth to where they are standing.
   *
   * Done here rather than inside the voice module because the voice module is
   * not allowed to know what a player is. It owns a decoder and a gain node per
   * speaker; the session owns the fact that those belong to a body at a
   * position, which is exactly the split that keeps voice unit-testable without
   * a mall.
   */
  private tickVoice(dt: number): void {
    const v = this.voice;
    if (!v) return;
    v.update();
    if (!this.voiceEmit.size) return;

    // The same five-a-second cadence the guards' occlusion runs at, for the
    // same reason: a raycast per speaker per frame buys nothing a player can
    // hear and costs something the renderer has already spent.
    this.voiceOcclude -= dt;
    const recheck = this.voiceOcclude <= 0;
    if (recheck) this.voiceOcclude = 0.2;

    for (const [id, em] of this.voiceEmit) {
      const a = this.crew.get(id);
      if (!a) continue;
      em.set(a.pos.x, a.pos.y + VOICE_MOUTH, a.pos.z);
      if (recheck) {
        em.occlusion = this.canSee(this.eye, {
          x: a.pos.x,
          y: a.pos.y + VOICE_MOUTH,
          z: a.pos.z,
        })
          ? 0
          : 1;
      }
    }
  }

  /**
   * The microphone panel, or nothing at all.
   *
   * Nothing in solo: a mic meter that can only ever hear you is a distraction
   * in the corner of a single-player round. In a room it is always shown, even
   * before the player has granted the microphone, because "press B" is the only
   * discoverable place to say the game has voice at all.
   */
  private voiceHud(): VoiceHud | null {
    const v = this.voice;
    if (!v || this.role === 'solo') return null;
    const talking: string[] = [];
    for (const a of this.crew.all) {
      if (!a.local && v.speaking(a.id)) talking.push(a.name);
    }
    return { live: v.stats.on && !v.muted, muted: v.muted, level: v.level, talking };
  }

  /**
   * Cameras, guards, and the one number that tells you how much trouble you are in.
   *
   * The detection bar is a blend rather than a max of two unrelated things,
   * because they mean different amounts of trouble. A guard's awareness IS the
   * trouble — at 1.0 they are running at you. A camera holding you is only a
   * report about to be filed, so it is weighted down: it must visibly move the
   * bar (otherwise standing in shot has no feedback at all and the camera might
   * as well not exist) without ever reading as "caught".
   */
  private tickGuards(dt: number): void {
    this.senses.length = 0;
    this.thieves.length = 0;
    const resample = this.exposureResample;
    for (const t of this.round.players.values()) {
      if (t.team !== 'thief') continue;
      this.thieves.push(t);
      // Stance comes off the actor, not off the local controller. This only
      // runs on the host, and on the host every thief has a real capsule with a
      // real frame — so a remote player sprinting past a torch is as loud and as
      // visible as the person holding the mouse, which is the whole point.
      const a = this.crew.get(t.id);
      this.senses.push({
        id: t.id,
        pos: t.pos,
        speed: a?.frame.speed ?? 0,
        crouching: a?.frame.crouching ?? false,
        active: t.status === 'active',
        exposure: this.exposureOf(t, resample),
      });
    }

    this.security.tick(dt, this.thieves, this.canSee);
    const corps = this.guards.update(dt, this.senses, this.security.activeContacts(), this.me);

    // The radio is heard from where the guard is standing, and only then read
    // out on the HUD. Guards repeat themselves when a search drags on; the
    // handset crackles every time but the toast does not.
    this.audio?.radio(corps.chatter);
    for (const say of corps.chatter) {
      // Sent before the repeat filter, because the filter is about this HUD and
      // the other machines have their own idea of what they last heard.
      this.pushNet({ t: 'radio', line: say.line, at: [say.at.x, say.at.y, say.at.z] });
      if (say.line === this.lastSay) continue;
      this.lastSay = say.line;
      this.hud.toast(say.line, 'bad', 4);
    }

    let cam = 0;
    for (const c of this.security.cameras) {
      if (c.looking !== this.me) continue;
      cam = Math.max(cam, Math.min(1, c.dwell / BALANCE.security.cameraDwellSec));
    }
    this.detection = Math.max(corps.detection, cam * 0.8);
  }

  /**
   * Step the light-sampling clock, and on a client sample the one body that
   * matters to it.
   *
   * Split out of `tickGuards` because that only runs on the host, and a client
   * that never sampled would read "fully lit" all night — the safest-looking
   * possible lie, since it would tell a player hiding in a dark alcove that
   * they are standing in a spotlight.
   */
  private tickExposure(): void {
    this.exposureResample = --this.exposureIn <= 0;
    if (this.exposureResample) this.exposureIn = EXPOSURE_TICKS;
    if (this.role !== 'client') return;
    const me = this.round.players.get(this.me);
    if (me && me.team === 'thief') this.exposureOf(me, this.exposureResample);
  }

  /**
   * WHAT THE BUILDING'S LIGHTING ACTUALLY OFFERS, MEASURED.
   *
   * `FULL_BRIGHT` in the exposure field is a calibration constant, and a
   * calibration constant chosen by eye off a screenshot is a guess. This walks
   * the nav grid — every patch of floor a player can actually stand on, not the
   * whole bounding box — and reports the distribution.
   *
   * The number to read is `dark`. If it is near zero the mechanic has nothing to
   * bite on: the mall is uniformly lit, the discount is always off, and the
   * player has been given a HUD readout they cannot change. If it is near one
   * the guards are blind. Somewhere around a third is the shape the reference
   * sheet describes — pools of light with a lot of nothing between them.
   *
   * Strided because there are tens of thousands of nodes and each sample can
   * cost six rays; the distribution of a seventh of the floor is the same
   * distribution.
   */
  exposureCensus(stride = 7): unknown {
    const f = this.exposureField;
    if (!f) return { error: 'no light rig' };
    const at = { x: 0, y: 0, z: 0 };
    const irr: number[] = [];
    const exp: number[] = [];
    for (let n = 0; n < this.nav.stats.nodes; n += stride) {
      this.nav.position(n, at);
      if (!this.nav.walkable(at)) continue;
      irr.push(f.irradiance(at.x, at.z, at.y));
      exp.push(f.exposure(at.x, at.z, at.y));
    }
    if (!exp.length) return { error: 'no walkable nodes' };
    irr.sort((a, b) => a - b);
    exp.sort((a, b) => a - b);
    const q = (a: number[], p: number) => Number(a[Math.min(a.length - 1, Math.floor(p * a.length))].toFixed(3));
    const frac = (lo: number, hi: number) =>
      Number(((exp.filter((v) => v >= lo && v < hi).length / exp.length) * 100).toFixed(1));
    return {
      lamps: f.lampCount,
      samples: exp.length,
      irradiance: { p10: q(irr, 0.1), p50: q(irr, 0.5), p90: q(irr, 0.9), max: q(irr, 0.999) },
      exposure: { p10: q(exp, 0.1), p50: q(exp, 0.5), p90: q(exp, 0.9) },
      // The three words the HUD prints, as percentages of the walkable floor.
      hidden: frac(0, 0.22 * BALANCE.guard.fullyLitAt),
      shaded: frac(0.22 * BALANCE.guard.fullyLitAt, 0.72 * BALANCE.guard.fullyLitAt),
      lit: frac(0.72 * BALANCE.guard.fullyLitAt, Infinity),
    };
  }

  /** How lit the local player is, 0..1, or null if the question does not apply. */
  private get myExposure(): number | null {
    if (!this.exposureField) return null;
    const me = this.round.players.get(this.me);
    if (!me || me.team !== 'thief') return null;
    return this.exposure.get(me.id) ?? 1;
  }

  /**
   * How lit a thief is, resampled every `EXPOSURE_TICKS` and eased in between.
   *
   * The easing is not cosmetic. Without it a thief walking under a batten goes
   * from 0.1 to 0.9 in a single frame, and since the HUD draws the same number
   * the indicator snaps rather than swells — which reads as a bug and, worse,
   * gives no warning that you are about to step into the light. A tenth of a
   * second of glide is enough to make the edge of a pool feel like an edge.
   */
  private exposureOf(t: PlayerState, resample: boolean): number {
    if (!this.exposureField) return 1;
    const was = this.exposure.get(t.id);
    if (was === undefined) {
      const now = this.exposureField.exposure(t.pos.x, t.pos.z, t.pos.y);
      this.exposure.set(t.id, now);
      return now;
    }
    if (!resample) return was;
    const now = this.exposureField.exposure(t.pos.x, t.pos.z, t.pos.y);
    const eased = was + (now - was) * EXPOSURE_EASE;
    this.exposure.set(t.id, eased);
    return eased;
  }

  /**
   * Can a camera at `from` see a thief at `to`?
   *
   * A bound arrow property rather than a method, because it is handed to the
   * security system as a plain function and gets called several times per tick.
   */
  private readonly canSee = (from: Vec3, to: Vec3): boolean => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d < 1e-3) return true;
    return this.physics.ray(from, { x: dx / d, y: dy / d, z: dz / d }, d - 0.2) === null;
  };

  /**
   * A key press, from whoever pressed it.
   *
   * `who` defaults to this machine's player, which is the only caller in solo and
   * the common one everywhere else. The host also calls it with a client's id
   * when an `act` message arrives, so there is exactly one implementation of what
   * dropping a crowbar means and it lives on the machine that owns the truth.
   */
  private onAction(action: string, who: string = this.me): void {
    // A client does not get to decide that it dropped a crowbar; it asks. The
    // reply arrives as an inventory event and a mesh on the floor, one round
    // trip later, which is imperceptible for something you press once a minute
    // and is the difference between an authoritative bag and a hopeful one.
    // Two exceptions, both for the same reason: they are things a player does to
    // their own machine, not to the mall. Asking the host for permission to
    // release your own mouse or close your own microphone would be absurd, and
    // in the microphone's case actively harmful — the one moment you reach for
    // that key is the moment you do not want a round trip's worth of breathing
    // going out.
    if (action === 'mute') {
      // Deliberately unconditional on `p`: a dead thief still talks, and being
      // dead is exactly when muting matters.
      if (who === this.me && this.voice) this.toggleMic();
      return;
    }
    if (this.role === 'client' && who === this.me && action !== 'cancel') {
      this.actionsOut.push(action);
      return;
    }
    const p = this.round.players.get(who);
    if (!p) return;
    const mine = who === this.me;
    switch (action) {
      case 'shove': {
        if (p.status !== 'active') break;
        // Thrown on the attempt, not on the hit. A shove that finds nothing but
        // air still has to look like a shove, or the miss reads as a dropped input.
        if (mine) this.viewmodel?.gesture('shove');
        const r = this.guards.shove(p.pos, who);
        this.audio?.shove(p.pos, r.ok);
        this.say(who, r.message, r.ok ? 'good' : 'bad', 2.5);
        break;
      }
      case 'cancel':
        // Purely a local window concern; a client releasing its own mouse is not
        // something the host has an opinion about.
        if (mine) this.input.releaseLock();
        break;
      case 'dropAll': {
        const spilled = this.round.dropCarried(who);
        for (const s of spilled) {
          this.attachLoose(s.id, s.item, s.pos);
          this.pushNet({ t: 'loose', id: s.id, item: s.item, at: [s.pos.x, s.pos.y, s.pos.z] });
        }
        if (spilled.length) {
          this.say(who, `Dropped ${spilled.length} items`, 'info', 2.5);
          this.invDirty.add(who);
        }
        break;
      }
      case 'drop': {
        // Sheds the single heaviest thing. The verb is "I need to move", and the
        // heaviest item is always the answer to that.
        let worst: ItemId | null = null;
        for (const s of p.carried) if (!worst || ITEMS[s.item].weight > ITEMS[worst].weight) worst = s.item;
        if (!worst) break;
        this.dropOne(worst, who);
        break;
      }
      case 'slot1':
      case 'slot2': {
        const tool = p.tools[action === 'slot1' ? 0 : 1];
        if (tool) this.dropOne(tool, who);
        break;
      }
      case 'use': {
        const con = p.carried.find((s) => ITEMS[s.item].kind === 'consumable');
        if (!con) {
          this.say(who, 'Nothing to use', 'info', 2);
          break;
        }
        if (useConsumable(p, con.item)) {
          this.say(who, ITEMS[con.item].name, 'good', 3);
          this.invDirty.add(who);
          if (mine) this.viewmodel?.gesture('use');
        }
        break;
      }
      default:
        break;
    }
  }

  private dropOne(id: ItemId, who: string = this.me): void {
    const p = this.round.players.get(who);
    if (!p) return;
    const ti = p.tools.indexOf(id);
    if (ti >= 0) p.tools.splice(ti, 1);
    else {
      const ci = p.carried.findIndex((s) => s.item === id);
      if (ci < 0) return;
      p.carried.splice(ci, 1);
    }
    // Drop it in front of you rather than inside you, or the pickup prompt
    // fights the fixture you are standing at. For a remote player "in front"
    // comes off their own body's facing, not this camera's.
    const a = this.crew.get(who);
    const fwd = who === this.me || !a?.body ? this.aim : a.body.forward(this.remoteAim);
    const at = { x: p.pos.x + fwd.x * 0.7, y: p.pos.y, z: p.pos.z + fwd.z * 0.7 };
    const looseId = this.round.addLoose(id, at);
    this.attachLoose(looseId, id, at);
    this.audio?.dropped(at);
    this.say(who, `Dropped ${ITEMS[id].name}`, 'info', 2.5);
    this.invDirty.add(who);
    this.pushNet({ t: 'loose', id: looseId, item: id, at: [at.x, at.y, at.z] });
  }

  private attachLoose(looseId: string, id: ItemId, pos: Vec3): void {
    const node = lootProp(id);
    node.position.set(pos.x, pos.y, pos.z);
    node.rotation.y = Math.random() * Math.PI * 2;
    this.stage.add(node);
    this.looseNodes.set(looseId, node);
    this.interaction.addLoose({ id: looseId, item: id, pos, store: null, node });
  }

  // -----------------------------------------------------------------------

  private onOutcome(r: InteractionResult): void {
    // At the fixture, not at the player — a shutter going up is heard from the
    // shopfront, and the guard two units over should be able to tell which one.
    this.applyOutcome(r, this.target?.at ?? this.player.position, null);
  }

  /**
   * What an interaction did to the world.
   *
   * Split from `onOutcome` because the host runs this for everybody. The world
   * half — a shutter opening, a route unblocking, a shelf going dark — has to
   * happen wherever it is simulated. The message half belongs to one person:
   * being told "Banked $4,000" because somebody on the other side of the mall
   * emptied a till is noise, and "Heard for 18m" is actively misleading when it
   * was not you who made the noise. `who` is null for the local player.
   */
  private applyOutcome(r: InteractionResult, at: Vector3, who: Actor | null): void {
    this.audio?.outcome(r, at);
    const mine = who === null;
    const byId = who?.id ?? this.me;
    switch (r.kind) {
      case 'refused':
        if (mine) this.hud.toast(r.message, 'bad', 2.5);
        else this.pushNet({ t: 'toast', text: r.message, tone: 'bad', for: byId });
        break;
      case 'breached':
        this.markOpen(r.id);
        // A shutter that has just gone up is a route. Guards must be able to use
        // the hole the thief made, or cutting into a shop is a free hiding place.
        this.nav.setBarrierOpen(r.id, true);
        if (r.noise > 0) {
          if (mine) this.hud.toast(`Heard for ${r.noise}m`, 'bad', 3);
          else this.pushNet({ t: 'toast', text: `Heard for ${r.noise}m`, tone: 'bad', for: byId });
        }
        this.pushNet({ t: 'breach', id: r.id, by: byId, noise: r.noise });
        break;
      case 'looted': {
        const def = item(r.item);
        this.emptyIfCleared(r.id);
        this.invDirty.add(byId);
        this.pushNet({
          t: 'looted',
          container: r.id,
          item: r.item,
          by: byId,
          cleared: (this.round.containers.get(r.id)?.contents.length ?? 0) === 0,
        });
        const line = def.kind === 'loot' ? `${def.name}  +$${def.value.toLocaleString()}` : def.name;
        if (mine) this.hud.toast(line, 'good', 2.5);
        else this.pushNet({ t: 'toast', text: line, tone: 'good', for: byId });
        break;
      }
      case 'deposited': {
        this.invDirty.add(byId);
        this.pushNet({
          t: 'banked',
          by: byId,
          amount: r.amount,
          items: r.items,
          at: [at.x, at.y, at.z],
        });
        const line = r.extraction
          ? `Out with $${r.amount.toLocaleString()}`
          : `Banked $${r.amount.toLocaleString()} · ${r.items} items`;
        if (mine) {
          this.hud.toast(line, 'good', 4);
        } else {
          this.pushNet({ t: 'toast', text: line, tone: 'good', for: byId });
          if (r.extraction) {
            // The one thing a crew genuinely needs to know about each other. A
            // thief reaching the van changes what everyone else should do next.
            const out = `${who.name} is out · $${r.amount.toLocaleString()}`;
            this.hud.toast(out, 'good', 4);
            this.pushNet({ t: 'toast', text: out, tone: 'good', not: byId });
          }
        }
        break;
      }
    }
  }

  /**
   * Turns a breached fixture green.
   *
   * The lock LED is the game's primary piece of readable information — from
   * across the atrium the colour tells you whether the thing in that shop needs
   * the tool you chose to carry. It therefore has to stop lying the instant the
   * lock is defeated, or a cleared store keeps advertising itself as a target.
   */
  private markOpen(id: string): void {
    const node =
      this.mall.containers.find((c) => c.id === id)?.node ??
      this.mall.barriers.find((b) => b.id === id)?.node;
    if (!node) return;
    node.traverse((o) => {
      if (o.name !== 'lockLed') return;
      const m = (o as unknown as { material: { color: { copy(c: unknown): void } } }).material;
      m.color.copy(LOCK_COLOR.none);
    });
  }

  /** Dims a shelf that has been stripped, so you do not walk back to it. */
  private emptyIfCleared(id: string): void {
    const state = this.round.containers.get(id);
    if (!state || state.contents.length > 0) return;
    const node = this.mall.containers.find((c) => c.id === id)?.node;
    node?.traverse((o) => {
      if (o.name !== 'lockLed') return;
      const m = (o as unknown as { material: { color: { multiplyScalar(s: number): void } } }).material;
      m.color.multiplyScalar(0.18);
    });
  }

  private onEvent(e: RoundEvent): void {
    this.audio?.event(e, e.t === 'guardDown' ? this.guards.guards.find((g) => g.id === e.guard)?.pos : undefined);
    switch (e.t) {
      case 'phase':
        if (e.phase === 'active') this.hud.toast('Doors open. Clock running.', 'info', 4);
        if (e.phase === 'results') this.showResults();
        this.pushNet({ t: 'phase', phase: PHASE_CODE[e.phase] });
        break;
      case 'capture':
        // The sim raises this for whoever was caught, including people whose
        // capsule this machine is only simulating on their behalf.
        this.say(e.thief, 'Caught.', 'bad', 8);
        this.invDirty.add(e.thief);
        this.pushNet({ t: 'capture', who: e.thief, at: [e.at.x, e.at.y, e.at.z] });
        // Everything they were carrying is now on the floor and lootable, so it
        // needs a mesh and a prompt — a rescue run at a friend's spilled bag is
        // one of the few reasons to walk back towards a guard.
        for (const d of e.dropped) {
          this.attachLoose(d.id, d.item, d.pos);
          this.pushNet({ t: 'loose', id: d.id, item: d.item, at: [d.pos.x, d.pos.y, d.pos.z] });
        }
        break;
      // Noise is not heard by ears — there are none. It is filed with the
      // security system as a contact from an unnamed source, which puts it on
      // the same footing as a camera sighting: a guard who is not looking at you
      // still gets told roughly where you were, and comes to check.
      case 'breach':
        this.security.reportNoise(e.by, this.round.players.get(e.by)?.pos ?? origin, `breach:${e.container}`);
        break;
      case 'noise':
        if (e.radius >= BALANCE.noise.run) this.security.reportNoise(e.by, e.at, 'noise');
        break;
      case 'guardDown': {
        this.say(e.by, 'Guard down. They get back up.', 'info', 3);
        const at = this.guards.guards.find((g) => g.id === e.guard)?.pos ?? origin;
        this.pushNet({ t: 'guardDown', guard: e.guard, at: [at.x, at.y, at.z] });
        break;
      }
      case 'end':
        break;
      default:
        break;
    }
  }

  private showResults(): void {
    this.input.releaseLock();
    const reason =
      this.round.reason === 'timer'
        ? 'The clock ran out.'
        : 'Nobody left inside.';
    const rows = this.round.scoreboard();
    const floor = this.round.floorValue();
    this.hud.showResults(rows, reason, this.me, this.reviewFor(rows, floor));
    this.pushNet({ t: 'score', rows, reason, floor });
    this.restartArmed = true;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.code !== 'KeyR' || !this.restartArmed) return;
      this.restartArmed = false;
      window.removeEventListener('keydown', onKey);
      this.restart();
    };
    window.addEventListener('keydown', onKey);
  }

  /**
   * Runs it again without reloading.
   *
   * Everything the round touched has to go back: the sim is thrown away and
   * rebuilt, every barrier collider is restored, every lock LED is re-coloured
   * from its own lock kind, and every loose mesh is deleted. Rebuilding the
   * physics world instead would be simpler and would cost a second of WASM
   * allocation on a machine that just spent one on the lightmapper.
   */
  restart(seed?: number): void {
    this.hud.hideResults();
    // The host mints the night; a client is handed one. If a client ever fell
    // back to minting its own, it would quietly play a different building from
    // everybody else in the lobby.
    const night = seed ?? this.pinnedSeed ?? mintSeed();
    // Sent first. Everything after this line invalidates ids the other machines
    // are still holding, and they need to be told to let go before we mint new
    // ones — the reset is the only event whose ORDER relative to the rest of the
    // stream matters, which is why the stream is reliable and ordered.
    this.pushNet({ t: 'reset', seed: night });
    for (const node of this.looseNodes.values()) node.removeFromParent();
    this.looseNodes.clear();

    for (const b of this.mall.barriers) {
      b.node.visible = true;
      this.physics.closeBarrier(b.id);
      this.nav.setBarrierOpen(b.id, false);
      this.recolour(b.node, b.lock);
    }
    for (const c of this.mall.containers) this.recolour(c.node, c.lock);

    const spawn = this.mall.spawns.thieves[0] ?? { x: 0, y: 0, z: 30 };
    (this as { round: Round }).round = this.newRound(spawn, night);
    this.interaction = new Interaction(this.mall, this.physics, this.round);
    this.layOutBench();
    this.player.teleport(spawn, 0);
    // The guards outlive the round object — their capsules and figures are
    // expensive and there is no reason to rebuild them — so they have to be
    // re-enrolled in the new round and walked back to the start of their beats.
    this.guards.reset(this.round);
    this.security.contacts.clear();
    this.detection = 0;
    // Or the first tick of the new night eases down from wherever the last one
    // ended, and a thief respawned at a dark bench reads as standing in a shop.
    this.exposure.clear();
    this.audio?.reset();
    this.hud.toast('New night.', 'info', 3);
  }

  /**
   * Reads the personal best off the machine and writes it back if tonight beat
   * it. This is the only state in the game that outlives the tab.
   *
   * Wrapped in a try because localStorage throws rather than degrades in a
   * sandboxed iframe and in private mode on some browsers, and a results screen
   * that crashes is a worse outcome than one that forgets.
   */
  private reviewFor(rows: ScoreLine[], floor: number): ResultsExtras {
    const banked = rows.find((r) => r.id === this.me)?.banked ?? 0;
    let best = 0;
    try {
      best = Number(localStorage.getItem(BEST_KEY)) || 0;
      if (banked > best) localStorage.setItem(BEST_KEY, String(banked));
    } catch {
      /* no storage; the card just shows tonight as the best there is */
    }
    return { name: this.round.name, floor, best, newBest: banked > best && banked > 0 };
  }

  private recolour(node: Object3D, lock: keyof typeof LOCK_COLOR): void {
    node.traverse((o) => {
      if (o.name !== 'lockLed') return;
      const m = (o as unknown as { material: { color: { copy(c: unknown): void } } }).material;
      m.color.copy(LOCK_COLOR[lock]);
    });
  }

  // -----------------------------------------------------------------------

  /** Hands the camera to the review overlay. The screenshot harness calls this. */
  suspend(): void {
    this.active = false;
    this.input.releaseLock();
    this.hud.update(0, {
      phase: this.round.phase,
      clock: this.round.clock,
      night: this.night,
      player: this.round.players.get(this.me)!,
      target: null,
      progress: 0,
      stamina: 1,
      paused: false,
      thievesLeft: 1,
      detection: this.detection,
      exposure: null,
      // Null, not the real thing: `suspend` exists so the screenshot harness can
      // photograph the mall, and a mic meter in the corner of every shot is
      // furniture in every review.
      voice: null,
      // Same reasoning, and one more: the waypoint is projected through the
      // player's camera, which the review overlay has just taken away.
      objective: null,
    });
  }

  resume(): void {
    this.active = true;
    this.player.teleport(this.round.players.get(this.me)!.pos, this.player.yaw);
  }

  /** What QA asserts on: the whole playable state, with no pixels involved. */
  get snapshot() {
    const p = this.round.players.get(this.me)!;
    const enc = encumbrance(p);
    return {
      phase: this.round.phase,
      clock: Number(this.round.clock.toFixed(1)),
      pos: [p.pos.x, p.pos.y, p.pos.z].map((v) => Number(v.toFixed(2))),
      banked: p.banked,
      carrying: p.carried.map((s) => s.item),
      tools: [...p.tools],
      kg: Number(enc.kg.toFixed(1)),
      tier: enc.tier,
      speed: Number(enc.speed.toFixed(2)),
      target: this.target
        ? { kind: this.target.kind, id: this.target.id, title: this.target.title, blocked: this.target.blocked }
        : null,
      progress: Number(this.interaction.progressFor(this.me).toFixed(2)),
      loose: this.round.loose.length,
      openContainers: [...this.round.containers.values()].filter((c) => c.open).length,
      totalContainers: this.round.containers.size,
      detection: Number(this.detection.toFixed(2)),
      exposure: Number((this.exposure.get(this.me) ?? 1).toFixed(2)),
      guards: this.guards.snapshot,
      contacts: this.security.activeContacts().length,
    };
  }

  /**
   * Drive the game from a script with no keyboard: the bot-client hook.
   *
   * QA needs to walk to a shop, cut a vitrine and post the haul without a human,
   * and the honest way to do that is to feed the same input the human does
   * rather than to call the sim directly — a test that skips the controller
   * cannot catch a container you physically cannot reach.
   */
  readonly bot = {
    lookAt: (x: number, z: number) => {
      const p = this.player.position;
      this.player.yaw = Math.atan2(-(x - p.x), -(z - p.z));
    },
    teleport: (x: number, y: number, z: number) => this.player.teleport({ x, y, z }),
    /** Tap a bound action — drop, use, dropAll, slot1, slot2. */
    press: (a: Action) => {
      this.input.inject(a);
      this.bot.step(1 / 30);
      return this.snapshot;
    },
    /** Runs `seconds` of simulated time at a fixed step with the given holds. */
    step: (seconds: number, hold: { forward?: number; strafe?: number; interact?: boolean } = {}) => {
      const STEP = 1 / 60;
      const src = this.input as unknown as { down: Set<string>; held: Set<string> };
      src.down.clear();
      if ((hold.forward ?? 0) > 0) src.down.add('KeyW');
      if ((hold.forward ?? 0) < 0) src.down.add('KeyS');
      if ((hold.strafe ?? 0) > 0) src.down.add('KeyD');
      if ((hold.strafe ?? 0) < 0) src.down.add('KeyA');
      if (hold.interact) src.held.add('interact');
      else src.held.delete('interact');
      const wasLocked = this.input.locked;
      this.input.locked = true;
      for (let t = 0; t < seconds; t += STEP) this.update(STEP);
      src.down.clear();
      src.held.delete('interact');
      // A key let go must be SEEN let go. Interaction refuses to roll onto a
      // new fixture until it observes a released frame — that is what stops one
      // long press sweeping the whole loadout bench. A script that holds
      // interact across two legs never gives it that frame, so the second leg
      // silently does nothing, which is a property of the harness and not of
      // the game. Releasing takes a frame for the bot exactly as it does for a
      // pair of hands.
      if (hold.interact) this.update(STEP);
      this.input.locked = wasLocked;
      return this.snapshot;
    },
  };
}

const origin: Vec3 = { x: 0, y: 0, z: 0 };

/**
 * Phase and status, as one byte each.
 *
 * Written out as paired tables rather than derived from one another, because
 * the wire format is a contract: a name that changes in the sim must break the
 * build here and force somebody to decide what old clients should see, rather
 * than silently shifting every code by one.
 */
/** Returned instead of a fresh `[]` on the overwhelmingly common empty drain. */
const EMPTY_EVENTS: NetEvent[] = [];
const EMPTY_ACTIONS: string[] = [];
const EMPTY_VOICE: ArrayBuffer[] = [];

/** Height of a mouth above a player's feet. Never zero, so a panner always has an angle. */
const VOICE_MOUTH = 1.6;
const PHASE_NAME: readonly RoundPhase[] = ['lobby', 'briefing', 'active', 'results'];
const PHASE_CODE: Record<RoundPhase, number> = { lobby: 0, briefing: 1, active: 2, results: 3 };

const STATUS_NAME: readonly (ThiefStatus | GuardStatus)[] = ['active', 'caught', 'extracted', 'downed'];
const STATUS_CODE: Record<ThiefStatus | GuardStatus, number> = {
  active: 0,
  caught: 1,
  extracted: 2,
  downed: 3,
};

/**
 * A camera plan is a place and a compass bearing; the sim wants a unit vector.
 *
 * Yaw 0 looks toward -Z, matching the player's, so this is the same forward
 * basis used everywhere else in the project. Getting it wrong points every
 * camera in the building at a wall and nothing ever reports anything.
 */
function cameraDef(c: CameraPlan): CameraDef {
  const pitch = c.pitch ?? 0;
  const cp = Math.cos(pitch);
  return {
    id: c.id,
    bank: c.bank,
    pos: { x: c.x, y: c.y, z: c.z },
    dir: { x: -Math.sin(c.yaw) * cp, y: Math.sin(pitch), z: -Math.cos(c.yaw) * cp },
    fovDeg: c.fovDeg,
    rangeM: c.rangeM,
  };
}

/** Exported for the HUD's benefit; the round's warn threshold is shared. */
export const WARN_AT = BALANCE.round.warnAtSec;
